import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "../../hooks";
import { WellProps } from "../../interfaces/interfaces";
import { AlertComponent, NavBar, ShowContent, Spinner } from "../../components";
import EditIcon from "@mui/icons-material/Edit";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconButton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DataContext from "../../context/DataContext";
import { useRequest } from "../../hooks";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

interface Props {
  id: number;
  name: string;
  wells: WellProps[];
  active: boolean;
  updatedAt: string;
}

interface FetchResponse {
  success?: boolean;
  data: Props;
}

const DEFAULT_PAGE_SIZE = 10;

// Sorts the FULL array using the same accessor a column declares via
// `sortAccessor`, before pagination slices it — mirrors ListPageLayout's own
// internal (uncontrolled) sort algorithm exactly, but is invoked here, over
// the complete filtered dataset, because this grid pages a fully-loaded
// local array (the client detail's wells) rather than a single
// already-paginated server page. Sorting only the already-sliced page
// (ListPageLayout's uncontrolled default) would silently leave the rest of
// the dataset unsorted relative to the visible page.
function sortRows<T>(
  rows: T[],
  sortBy: string | null,
  sortDir: "asc" | "desc",
  columns: ListColumn<T>[],
): T[] {
  if (!sortBy) return rows;
  const col = columns.find((c) => c.key === sortBy);
  if (!col?.sortAccessor) return rows;
  return [...rows].sort((a, b) => {
    const av = col.sortAccessor!(a);
    const bv = col.sortAccessor!(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
}

export const ClientDetail = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { idClient } = useParams();
  const { data, error, isLoading } = useFetch<FetchResponse>(
    `client-detail/${idClient}`
  );
  const [clientName, setClientName] = useState("");
  const [clientStatus, setClientStatus] = useState("Active");
  const [clientUsers, setClientUsers] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const { isSuccess, isError, onEditClient } = useContext(DataContext);

  const wells = useMemo(() => data?.data?.wells ?? [], [data]);
  const [wellSearch, setWellSearch] = useState("");
  const [wellPage, setWellPage] = useState(0);
  const [wellPageSize, setWellPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [wellSortBy, setWellSortBy] = useState<string | null>(null);
  const [wellSortDir, setWellSortDir] = useState<"asc" | "desc">("asc");

  const handleWellSortChange = (key: string) => {
    setWellSortDir((prev) => (wellSortBy === key && prev === "asc" ? "desc" : "asc"));
    setWellSortBy(key);
  };

  useEffect(() => {
    if (data) {
      const {
        data: { name, active },
      } = data;
      setClientName(name);
      active ? setClientStatus("Active") : setClientStatus("Inactive");
    }
  }, [data]);

  const fetchClientUsers = () => {
    if (!idClient) return;
    const options: RequestInit = { method: "GET" };
    Promise.all([
      handleRequest({ endpoint: "user", options }),
      handleRequest({ endpoint: `user/client/${idClient}`, options }),
    ]).then(([usersRes, assignedRes]) => {
      const assigned = assignedRes.success ? assignedRes.data : [];
      setClientUsers(assigned);
      const assignedIds = new Set(assigned.map((user: any) => user.id));
      setUserOptions(
        (usersRes.success ? usersRes.data : []).filter(
          (user: any) =>
            user.active && user.idRol === 3 && !assignedIds.has(user.id),
        ),
      );
    });
  };

  useEffect(() => {
    fetchClientUsers();
  }, [idClient]);

  const assignUser = () => {
    if (!selectedUser || !idClient) return;
    handleRequest({
      endpoint: "user-client",
      options: {
        method: "POST",
        body: JSON.stringify({
          userId: selectedUser.id,
          clientId: parseInt(idClient),
        }),
      },
    }).then(() => {
      setSelectedUser(null);
      fetchClientUsers();
    });
  };

  const removeUser = (userId: number) => {
    if (!idClient) return;
    handleRequest({
      endpoint: `user-client/${userId}/${idClient}`,
      options: { method: "DELETE" },
    }).then(() => fetchClientUsers());
  };

  const buttons = [
    {
      title: "Edit",
      action: () =>
        idClient &&
        onEditClient(
          parseInt(idClient),
          clientName,
          clientStatus === "Active" ? true : false
        ),
      icon: <EditIcon />,
      disabled: !idClient,
    },
  ];

  const wellColumns: ListColumn<WellProps>[] = [
    {
      key: "name",
      header: "Well",
      render: (r) => r.name || "",
      sortAccessor: (r) => r.name ?? "",
      width: "2fr",
    },
    {
      key: "contact",
      header: "Contact",
      render: (r) => r.contact,
      sortAccessor: (r) => r.contact ?? "",
      width: "1.4fr",
    },
    {
      key: "phoneNumber",
      header: "Phone Number",
      render: (r) => r.phoneNumber,
      sortAccessor: (r) => r.phoneNumber ?? "",
      width: "1.2fr",
    },
  ];

  const filteredWells = useMemo(() => {
    const q = wellSearch.trim().toLowerCase();
    return q
      ? wells.filter((r) => r.name?.toLowerCase().includes(q))
      : wells;
  }, [wells, wellSearch]);

  // Sort the FULL filtered array first (see sortRows' comment above for why),
  // THEN slice to the current page — so paging through a sorted list is
  // correct relative to the whole dataset, not just whatever rows happened to
  // land on that page beforehand.
  const sortedWells = sortRows(filteredWells, wellSortBy, wellSortDir, wellColumns);

  const pagedWells = sortedWells.slice(
    wellPage * wellPageSize,
    wellPage * wellPageSize + wellPageSize,
  );

  let content: JSX.Element | JSX.Element[];

  if (!data) {
    content = <Spinner />;
  } else {
    content = (
      <>
        <NavBar title={clientName} buttons={buttons} />
        <Box
          sx={{
            marginTop: "80px",
            minHeight: "calc(100vh - 80px)",
            background:
              "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
            padding: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Fade in={true} timeout={600}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 3 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "rgb(140, 150, 169)",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 1,
                    }}
                  >
                    Client Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item lg={6} xs={12}>
                      <TextField
                        label="Client Name"
                        variant="outlined"
                        value={clientName}
                        fullWidth
                        onChange={(e) => setClientName(e.target.value)}
                      />
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <Autocomplete
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        )}
                        value={clientStatus}
                        onChange={(_, newValue) =>
                          (newValue === "Active" || newValue === "Inactive") &&
                          setClientStatus(newValue)
                        }
                        options={["Active", "Inactive"]}
                        disablePortal
                        fullWidth
                        renderInput={(params) => (
                          <TextField {...params} label="Status" />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 3 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "rgb(140, 150, 169)",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 1,
                    }}
                  >
                    Assigned Users
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} (${option.email})`
                            : ""
                        }
                        onChange={(_, value) => setSelectedUser(value)}
                        options={userOptions}
                        value={selectedUser}
                        renderInput={(params) => (
                          <TextField {...params} label="Client user" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        disabled={!selectedUser}
                        onClick={assignUser}
                        variant="contained"
                      >
                        Assign user
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {clientUsers.map((user) => (
                          <Chip
                            key={user.id}
                            label={`${user.name} (${user.email})`}
                            onDelete={() => removeUser(user.id)}
                          />
                        ))}
                        {!clientUsers.length && (
                          <Typography color="text.secondary" variant="body2">
                            No client users assigned.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    padding: { xs: 2, md: 4 },
                    borderRadius: "20px",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(140, 150, 169, 0.1)",
                    boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                  }}
                >
                  <ListPageLayout<WellProps>
                    title="Wells"
                    columns={wellColumns}
                    rows={pagedWells}
                    getRowId={(r) => r.id!}
                    rowsLoading={false}
                    searchValue={wellSearch}
                    onSearchChange={setWellSearch}
                    searchPlaceholder="Search wells…"
                    sortBy={wellSortBy}
                    sortDir={wellSortDir}
                    onSortChange={handleWellSortChange}
                    page={wellPage}
                    pageSize={wellPageSize}
                    totalCount={filteredWells.length}
                    onPageChange={setWellPage}
                    onPageSizeChange={(pageSize) => {
                      setWellPageSize(pageSize);
                      setWellPage(0);
                    }}
                    rowActions={(r) => (
                      <Tooltip title="Open Well">
                        <IconButton
                          onClick={() => navigate(`/well/${r.id}`)}
                          size="small"
                          aria-label="Open well detail"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </Paper>
              </Box>
            </Fade>
          </Container>
        </Box>
        {isSuccess && <AlertComponent type="success" />}
        {isError && <AlertComponent type="error" />}
      </>
    );
  }

  return <ShowContent error={error} isLoading={isLoading} content={content} />;
};
